import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Calculator, 
  FileText, 
  ShieldCheck, 
  TrendingUp, 
  Users, 
  Building2,
  ArrowRight,
  CheckCircle,
  Award,
  Clock
} from 'lucide-react';

export const HomePage = () => {
  const stats = [
    { value: '150K+', label: 'Projects Done', icon: Calculator },
    { value: '25K+', label: 'Happy Clients', icon: Users },
    { value: '120+', label: 'Expert Staff', icon: Award },
    { value: '15+', label: 'Years Experience', icon: Clock },
  ];

  const services = [
    {
      icon: FileText,
      title: 'Income Tax Filing',
      description: 'Expert assistance for individual and business income tax filing with maximum refunds.',
      featured: true,
    },
    {
      icon: Calculator,
      title: 'GST Filing',
      description: 'Comprehensive GST registration, filing, and compliance services.',
    },
    {
      icon: Building2,
      title: 'Business Registration',
      description: 'Quick and hassle-free company registration and incorporation services.',
    },
    {
      icon: TrendingUp,
      title: 'Tax Planning',
      description: 'Strategic tax planning to minimize liabilities and maximize savings.',
    },
    {
      icon: ShieldCheck,
      title: 'Audit Services',
      description: 'Professional audit services ensuring compliance and accuracy.',
    },
    {
      icon: FileText,
      title: 'Compliance Services',
      description: 'Stay compliant with all regulatory requirements and avoid penalties.',
    },
  ];

  const whyChooseUs = [
    'Expert team of certified professionals',
    '24/7 customer support',
    'Transparent pricing with no hidden costs',
    'Quick turnaround time',
    '100% accuracy guarantee',
    'Secure data handling',
  ];

  const trustedCompanies = [
    'Finance Corp', 'Salford Inc', 'Pixeled Tech', 'Exoru Systems', 'Nova Group'
  ];

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center overflow-hidden"
      >
        {/* Background */}
        <div className="absolute inset-0 bg-navy-900">
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/95 to-navy-900/70" />
          {/* Decorative Elements */}
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <span className="inline-block px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium">
                  Trusted by 25,000+ Clients
                </span>
                <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
                  Smart Tax Solutions for{' '}
                  <span className="text-gradient">Modern Business</span>
                </h1>
                <p className="text-lg text-slate-400 max-w-xl leading-relaxed">
                  Expert tax filing, GST compliance, and business registration services. 
                  Let our professionals handle your finances while you focus on growth.
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button 
                    data-testid="hero-cta-primary"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg font-medium group"
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button 
                    data-testid="hero-cta-secondary"
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg"
                  >
                    Learn More
                  </Button>
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="pt-8">
                <p className="text-slate-500 text-sm mb-4">Trusted by leading companies</p>
                <div className="flex flex-wrap gap-6">
                  {trustedCompanies.map((company, index) => (
                    <span 
                      key={company}
                      className="text-slate-400 font-medium opacity-60 hover:opacity-100 transition-opacity"
                    >
                      {company}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Hero Image/Card */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-sky-500/20 rounded-3xl blur-2xl" />
                <div className="relative bg-navy-800/50 backdrop-blur-xl border border-white/10 rounded-3xl p-8 space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Calculator className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-white">Quick Tax Calculator</h3>
                      <p className="text-slate-400 text-sm">Estimate your tax savings</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {stats.slice(0, 2).map((stat) => (
                      <div key={stat.label} className="bg-navy-900/50 rounded-xl p-4 text-center">
                        <p className="font-heading font-bold text-2xl text-blue-400">{stat.value}</p>
                        <p className="text-slate-400 text-sm">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                  <Link to="/contact">
                    <Button className="w-full bg-blue-500 hover:bg-blue-600 text-white">
                      Book Free Consultation
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section 
        data-testid="stats-section"
        className="bg-navy-800 border-y border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={stat.label}
                data-testid={`stat-${index}`}
                className="text-center group"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-navy-900 border border-white/10 flex items-center justify-center group-hover:border-blue-500/50 transition-colors">
                  <stat.icon className="w-7 h-7 text-blue-400" />
                </div>
                <p className="font-heading font-bold text-3xl md:text-4xl text-white mb-1">
                  {stat.value}
                </p>
                <p className="text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section 
        data-testid="services-section"
        className="py-20 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-blue-400 font-medium mb-4 block">Our Services</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Comprehensive Tax Solutions
            </h2>
            <p className="text-slate-400">
              From individual tax filing to complex business compliance, we've got you covered.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={service.title}
                data-testid={`service-card-${index}`}
                className={`bg-navy-800 border border-white/5 rounded-2xl p-8 card-hover group ${
                  service.featured ? 'md:col-span-2 lg:col-span-1 lg:row-span-1' : ''
                }`}
              >
                <div className="w-14 h-14 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-6 group-hover:bg-blue-500/20 transition-colors">
                  <service.icon className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section 
        data-testid="why-choose-section"
        className="py-20 md:py-32 bg-navy-800/50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-blue-400 font-medium mb-4 block">Why Choose Us</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
                Trusted by Thousands of Businesses
              </h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                With over 15 years of experience, we have helped thousands of individuals 
                and businesses with their tax and compliance needs. Our team of certified 
                professionals ensures accuracy and maximum benefits for our clients.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {whyChooseUs.map((item, index) => (
                  <div 
                    key={index}
                    data-testid={`feature-${index}`}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact" className="inline-block mt-8">
                <Button 
                  data-testid="why-choose-cta"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-8"
                >
                  Contact Us Today
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 to-sky-500/10 rounded-3xl blur-2xl" />
              <img
                src="https://images.pexels.com/photos/7654129/pexels-photo-7654129.jpeg?w=800&q=80"
                alt="Tax consultation"
                className="relative rounded-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        data-testid="cta-section"
        className="py-20 md:py-32"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="bg-gradient-to-r from-blue-500/10 to-sky-500/10 border border-white/10 rounded-3xl p-12 md:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-sky-500/10 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
                Ready to Simplify Your Taxes?
              </h2>
              <p className="text-slate-400 mb-8 max-w-xl mx-auto">
                Get expert assistance from our certified professionals. 
                Book a free consultation today and discover how we can help.
              </p>
              <Link to="/contact">
                <Button 
                  data-testid="cta-button"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-10 py-6 text-lg"
                >
                  Book Free Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
